import React, { useEffect } from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  IonBadge,
  useIonViewWillLeave,
} from '@ionic/react';
import { checkmarkCircleOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from '@/modules/idv';
import { PhoneLogic } from '@/modules/phone';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { pxToRem } from '@/modules/config/constants';

const SummaryStep: React.FC = () => {
  // The summary reflects only COMMITTED (verified) values, never the wizard's
  // in-progress buffers — by the time we reach it, any unverified phone/email
  // edit is given up on. So we read name/phone/email from UserContext (committed)
  // and, on mount, discard any pending change so nothing unverified is shown or
  // later promoted (including if the user navigates back to an earlier step).
  const {
    claims,
    familiarName,
    allowedName,
    preferredPronouns,
    phone: committedPhone,
    email: committedEmail,
    discardPendingChanges,
  } = useUserContext();
  const {
    setPhone: setWizardPhone,
    setEmail: setWizardEmail,
    exitWizard,
    navigateToStep,
    progress,
  } = useProfileWizardContext();

  useEffect(() => {
    let cancelled = false;
    discardPendingChanges()
      .then(({ phone, email }) => {
        if (cancelled) return;
        // Sync the wizard's input buffers to the committed values so navigating
        // back from the summary doesn't resurrect the abandoned edit.
        setWizardPhone(phone ? PhoneLogic.format(phone.value) : '');
        setWizardEmail(email ?? '');
      })
      .catch(() => {
        // Best-effort: a storage hiccup shouldn't block the summary.
      });
    return () => {
      cancelled = true;
    };
  }, [discardPendingChanges, setWizardPhone, setWizardEmail]);

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const handleDone = async () => {
    await exitWizard();
  };

  const phoneVerified = ClaimsLogic.isPhoneVerified(claims);
  const emailVerified = ClaimsLogic.isEmailVerified(claims);
  // Only claim a value "expired" when it was actually verified once and has
  // since lapsed. A value the user entered but never verified (e.g. canceled
  // the IDV modal) has no expiration and is shown as "Unverified" instead.
  const phoneExpired = ClaimsLogic.isPhoneExpired(claims);
  const emailExpired = ClaimsLogic.isEmailExpired(claims);

  const displayFamiliarName = familiarName || 'Not set';
  const displayAllowedName = allowedName || 'Not set';
  const displayPronouns = preferredPronouns || 'Not set';
  const displayPhone = committedPhone
    ? PhoneLogic.format(committedPhone.value)
    : 'Not set';
  const displayEmail = committedEmail || 'Not set';

  // Field labels are gray; field values are the default (dark/black) text color.
  const fieldLabelStyle: React.CSSProperties = {
    color: 'var(--ion-color-medium)',
  };
  const fieldValueStyle: React.CSSProperties = {
    color: 'var(--ion-text-color)',
  };

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('summary')}
        progress={progress}
        onBack={() =>
          navigateToStep(ProfileWizardLogic.previousStep('summary'), 'back')
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
              Here's what we have for you.
            </p>
          </IonText>
          <IonItem>
            <IonLabel>
              <h3 style={fieldLabelStyle}>Familiar Name</h3>
              <p style={fieldValueStyle}>{displayFamiliarName}</p>
              <IonText color="medium">
                <p style={{ fontSize: pxToRem(12) }}>
                  (stored only on your device)
                </p>
              </IonText>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonLabel>
              <h3 style={fieldLabelStyle}>Allowed Name</h3>
              <p style={fieldValueStyle}>{displayAllowedName}</p>
              <IonText color="medium">
                <p style={{ fontSize: pxToRem(12) }}>
                  (stored only on your device)
                </p>
              </IonText>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonLabel>
              <h3 style={fieldLabelStyle}>Pronouns</h3>
              <p style={fieldValueStyle}>{displayPronouns}</p>
              <IonText color="medium">
                <p style={{ fontSize: pxToRem(12) }}>
                  (stored only on your device)
                </p>
              </IonText>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonLabel>
              <h3 style={fieldLabelStyle}>Phone</h3>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <p style={{ margin: 0, ...fieldValueStyle }}>{displayPhone}</p>
                {phoneVerified && (
                  <IonBadge color="success">
                    <IonIcon
                      icon={checkmarkCircleOutline}
                      style={{ marginRight: '4px' }}
                    />
                    Verified
                  </IonBadge>
                )}
                {displayPhone !== 'Not set' && !phoneVerified && (
                  <IonBadge color="warning">
                    {phoneExpired ? 'Expired' : 'Unverified'}
                  </IonBadge>
                )}
              </div>
              <IonText color="medium">
                <p style={{ fontSize: pxToRem(12) }}>
                  (stored on your device, encrypted on our server)
                </p>
              </IonText>
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonLabel>
              <h3 style={fieldLabelStyle}>Email</h3>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <p style={{ margin: 0, ...fieldValueStyle }}>{displayEmail}</p>
                {emailVerified && (
                  <IonBadge color="success">
                    <IonIcon
                      icon={checkmarkCircleOutline}
                      style={{ marginRight: '4px' }}
                    />
                    Verified
                  </IonBadge>
                )}
                {displayEmail !== 'Not set' && !emailVerified && (
                  <IonBadge color="warning">
                    {emailExpired ? 'Expired' : 'Unverified'}
                  </IonBadge>
                )}
              </div>
              <IonText color="medium">
                <p style={{ fontSize: pxToRem(12) }}>(stored on our server)</p>
              </IonText>
            </IonLabel>
          </IonItem>
        </div>
      </IonContent>
      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleDone}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            Done
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    </IonPage>
  );
};

export default SummaryStep;
